/*
  © 2025 FalconTheme
  https://www.falcontheme.com
*/

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.button = this.form.querySelector('button[name="add"]');
    this.form.addEventListener("submit", this.onSubmit.bind(this));
    this.formData = new FormData(this.form);
  }

  async onSubmit(event) {
    event.preventDefault();

    this.formData = new FormData(this.form);

    this.addQtyBreaks();
    this.addUpsells();

    this.button.classList.add("loading");
    this.button.disabled = true;
    this.button.setAttribute("aria-busy", "true");

    await document.querySelector("cart-component").add(this.formData);

    this.button.classList.remove("loading");
    this.button.disabled = false;
    this.button.setAttribute("aria-busy", "false");
  }

  addQtyBreaks() {
    let variantIds = this.querySelector('input[name*="qty-breaks"]:checked')?.value;
    if (!variantIds) return;

    const properties = {};
    for (const [key, value] of this.formData.entries()) {
      if (key.startsWith("properties[")) {
        const cleanKey = key.replace("properties[", "").replace("]", "");
        properties[cleanKey] = value;
      }
    }

    Object.keys(properties).forEach((p) => {
      this.formData.delete(`properties[${p}]`);
    });


    this.formData.delete("id");
    this.formData.delete("quantity");

    variantIds = variantIds.split(",");

    variantIds.forEach((variant, index) => {
      this.formData.append(`items[qty-breaks-${index}][id]`, variant);
      this.formData.append(`items[qty-breaks-${index}][quantity]`, 1);

      Object.keys(properties).forEach((p) => {
        this.formData.append(`items[qty-breaks-${index}][properties][${p}]`, properties[p]);
      });
    });
  }

  addUpsells() {
    const upsells = Array.from(this.form.querySelectorAll('[name*="upsell"]')).reduce((acc, input) => {
      if (input.checked) {
        acc.push({
          id: Number(input.value),
          quantity: Number(input.dataset.quantity || 1),
        });
      }
      return acc;
    }, []);

    upsells.forEach((item, index) => {
      this.formData.append(`items[upsells-${index}][id]`, item.id);
      this.formData.append(`items[upsells-${index}][quantity]`, item.quantity);
    });
  }
}
customElements.define("product-form", ProductForm);

class ProductOptions extends HTMLElement {
  constructor() {
    super();

    this.form = this.closest("form");
    this.variantsData = this.getVariantsData();
    this.productOptions = this.querySelectorAll('[name*="option"]');

    this.productOptions.forEach((option) => option.addEventListener("change", this.onChange.bind(this)));

    this.handleOptionsAvailability();
  }

  getVariantsData() {
    const script = this.querySelector("[data-product-variants-json]");
    if (!script) return null;
    return JSON.parse(script.textContent);
  }

  async onChange() {
    const selectedOptions = Array.from(this.productOptions)
      .filter((elem) => (elem.type === "radio" && elem.checked) || elem.type === "select-one")
      .map((elem) => elem.value);

    const selectedVariant = this.variantsData.find((variant) => JSON.stringify(variant.options) === JSON.stringify(selectedOptions));
    console.log("selectedVariant", selectedVariant);

    this.adjustButtons(selectedVariant);

    if (selectedVariant) {
      this.form.querySelector('[name="id"]').value = selectedVariant.id;

      if (this.closest('body[data-page-type="product"]')) {
        const url = new URL(window.location);
        url.searchParams.set("variant", selectedVariant.id);
        window.history.replaceState({}, "", url);
      }

      const colorSwatchesSelectedValue = this.form.querySelector(".color-swatches-selected-value");
      if (colorSwatchesSelectedValue) {
        colorSwatchesSelectedValue.textContent = this.form.querySelector('.color-swatches input:checked')?.value
      }

      this.handleOptionsAvailability();
      this.adjustPricing(selectedVariant);

      window.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: selectedVariant,
        }),
      );

      const respoonse = await fetch(`${this.form.dataset.productUrl}?variant=${selectedVariant.id}`);
      const text = await respoonse.text();
      const newDoc = new DOMParser().parseFromString(text, "text/html");

      window.dispatchEvent(
        new CustomEvent("variant:newDoc", {
          detail: newDoc,
        }),
      );
    }
  }

  adjustPricing(selectedVariant) {
    if (!selectedVariant) return;

    const priceContainer = this.form.querySelector(".price");
    if (!priceContainer) return;

    priceContainer.setAttribute("data-has-price-compare", selectedVariant.compare_at_price > selectedVariant.price);
    priceContainer.setAttribute("data-sold-out", !selectedVariant.available);

    priceContainer.querySelectorAll("[data-compare-price]").forEach((elem) => {
      elem.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price);
    });

    priceContainer.querySelectorAll("[data-price]").forEach((elem) => {
      elem.textContent = window.Shopify.formatMoney(selectedVariant.price);
    });

    priceContainer.querySelectorAll(".badge-sale").forEach((elem) => {
      const text = elem.dataset.text;
      const discountType = elem.dataset.discountType;
      let savings = "";
      if (discountType === "percentage") {
        savings = 100 - Math.round((selectedVariant.price / selectedVariant.compare_at_price) * 100) + "%";
      } else {
        savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price);
      }
      elem.textContent = text.replace("{{ value }}", savings);
    });
  }

  adjustButtons(selectedVariant) {
    const atcBtn = this.form.querySelector('button[name="add"]');
    const buyBtn = this.form.querySelector('button[name="buy"]');

    if (selectedVariant) {
      if (atcBtn) {
        atcBtn.textContent = selectedVariant.available ? atcBtn.dataset.textAdd : atcBtn.dataset.textSoldOut;
        atcBtn.disabled = !selectedVariant.available;
      }
      if (buyBtn) {
        buyBtn.disabled = !selectedVariant.available;
      }
    } else {
      if (atcBtn) {
        atcBtn.textContent = atcBtn.dataset.textUnavailable;
      }
    }
  }

  handleOptionsAvailability() {
    const selectedOption1 = this.form.querySelector('input[data-option-position="1"]:checked')?.value;
    const selectedOption2 = this.form.querySelector('input[data-option-position="2"]:checked')?.value;
    const selectedOption3 = this.form.querySelector('input[data-option-position="3"]:checked')?.value;

    this.form.querySelectorAll('input[data-option-position="1"]').forEach((input) => {
      const correspondingVariant = this.variantsData.find((variant) => {
        if (selectedOption3) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2 && variant.option3 === selectedOption3) return variant;
        } else if (selectedOption2) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2) return variant;
        } else {
          if (variant.option1 === input.value) return variant;
        }
        return null;
      });

      if (correspondingVariant?.available) {
        input.classList.remove("disabled");
        input.removeAttribute("aria-disabled");
      } else {
        input.classList.add("disabled");
        input.setAttribute("aria-disabled", "true");
      }
    });

    this.form.querySelectorAll('input[data-option-position="2"]').forEach((input) => {
      const correspondingVariant = this.variantsData.find((variant) => {
        if (selectedOption3) {
          if (variant.option2 === input.value && variant.option1 === selectedOption1 && variant.option3 === selectedOption3) return variant;
        } else {
          if (variant.option2 === input.value && variant.option1 === selectedOption1) return variant;
        }
        return null;
      });

      if (correspondingVariant?.available) {
        input.classList.remove("disabled");
        input.removeAttribute("aria-disabled");
      } else {
        input.classList.add("disabled");
        input.setAttribute("aria-disabled", "true");
      }
    });

    this.form.querySelectorAll('input[data-option-position="3"]').forEach((input) => {
      const correspondingVariant = this.variantsData.find((variant) => {
        if (variant.option3 === input.value && variant.option1 === selectedOption1 && variant.option2 === selectedOption2) return variant;
        return null;
      });

      if (correspondingVariant?.available) {
        input.classList.remove("disabled");
        input.removeAttribute("aria-disabled");
      } else {
        input.classList.add("disabled");
        input.setAttribute("aria-disabled", "true");
      }
    });
  }
}
customElements.define("product-options", ProductOptions);

class ProductBuyButton extends HTMLElement {
  constructor() {
    super();

    this.btn = this.querySelector('button[name="buy"]');
    this.form = this.closest("form");

    this.btn.addEventListener("click", this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();

    this.btn.classList.add("loading");
    const variantId = this.form.querySelector('[name="id"]').value;
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1);

    let items = [
      { id: variantId, quantity: qty }
    ];

    const qtyBreaks = this.closest('.product-form').querySelector('product-qty-breaks');

    if (qtyBreaks) {
      const variantIds = qtyBreaks.querySelector('input[name*="qty-breaks"]:checked')?.value;
      if (variantIds) {
        items = [];
        variantIds.split(",").forEach((id) => {
          items.unshift({ id: id, quantity: 1 });
        });
      }
    }

    this.closest('.product-form')?.querySelectorAll('[name*="upsell"]').forEach((input) => {
      if (input.checked) {
        items.unshift({ id: input.value, quantity: input.dataset.quantity || 1 });
      }
    });

    window.location.href = `${this.dataset.cartUrl}/${items.map(item => `${item.id}:${item.quantity}`).join(',')}`;

    setTimeout(() => {
      this.btn.classList.remove("loading");
    }, 3000);
  }
  
}
customElements.define("product-buy-button", ProductBuyButton);

class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = Number(this.dataset.initialIndex);

    this.track = this.querySelector(".product-media-gallery-track");
    this.allSlides = Array.from(this.querySelectorAll(".product-media-gallery-slide"));
    this.allThumbs = Array.from(this.querySelectorAll(".product-media-gallery-thumbs button"));
    this.slides = this.allSlides;
    this.controls = this.querySelectorAll(".product-media-gallery-control");
    this.pagination = this.querySelector(".product-media-gallery-pagination");
    this.thumbs = this.allThumbs;
    this.colorOptionPosition = Number(this.dataset.colorOptionPosition || 0);

    this.handleControls();
    this.updatePagination();
    this.handleThumbs();
    this.handleTouch();
    this.handleKeyboard();
    this.handleSticky();
    this.handleModalGallery();
    this.handleVariantChange();

    if (this.dataset.initialColor) {
      this.filterByColor(this.dataset.initialColor);
    }

    const initialSlideEl = this.allSlides[this.currentIndex] || this.allSlides[0];
    const mappedIndex = this.slides.indexOf(initialSlideEl);
    this.go(mappedIndex !== -1 ? mappedIndex : 0);
    this.setAttribute("data-init", "");
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.update();
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.update();
  }

  go(index) {
    this.currentIndex = index;
    this.update();
  }

  // Shows only the slides/thumbs whose data-media-color matches the given
  // color (case-insensitive, substring match on the image's alt text).
  // If nothing matches (product has no color option, or photos aren't
  // tagged with a color yet), everything stays visible - so this never
  // breaks products that don't use this convention.
  filterByColor(color) {
    const normalized = (color || "").trim().toLowerCase();
    let matched = this.allSlides;

    if (normalized) {
      const bySameColor = this.allSlides.filter((slide) => {
        const slideColor = (slide.dataset.mediaColor || "").toLowerCase();
        return slideColor && (slideColor === normalized || slideColor.includes(normalized) || normalized.includes(slideColor));
      });
      if (bySameColor.length) matched = bySameColor;
    }

    this.allSlides.forEach((slide) => {
      slide.style.display = matched.includes(slide) ? "" : "none";
    });

    this.allThumbs.forEach((thumb, i) => {
      const wrapper = thumb.closest(".product-media-gallery-thumb") || thumb;
      wrapper.style.display = matched.includes(this.allSlides[i]) ? "" : "none";
    });

    this.slides = matched;
    this.thumbs = this.allThumbs.filter((thumb, i) => matched.includes(this.allSlides[i]));

    this.updatePagination();
  }

  update() {
    const offset = -this.currentIndex * 100;
    this.track.style.transform = `translateX(${offset}%)`;

    this.slides.forEach((slide, index) => {
      slide.classList.toggle("active", index === this.currentIndex);
      slide.setAttribute("aria-hidden", index !== this.currentIndex);
      slide.querySelector("a")?.setAttribute("tabindex", index === this.currentIndex ? "0" : "-1");
    });

    this.updatePagination();
    this.updateThumbs();
    this.updateThumbsScroll();
  }

  handleControls() {
    this.controls.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.name === "previous") {
          this.prev();
        } else {
          this.next();
        }
      });
    });
  }

  updatePagination() {
    if (!this.pagination) return;
    this.pagination.textContent = `${this.currentIndex + 1}/${this.slides.length}`;
  }

  handleThumbs() {
    this.allThumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const index = this.thumbs.indexOf(thumb);
        if (index !== -1) this.go(index);
      });
    });
  }

  updateThumbs() {
    this.thumbs.forEach((thumb, index) => {
      thumb.classList.toggle("active", index === this.currentIndex);
      thumb.setAttribute("aria-selected", index === this.currentIndex);
    });
  }

  updateThumbsScroll() {
    const track = this.querySelector(".product-media-gallery-thumbs");
    if (!track) return;

    const activeThumb = this.querySelector(".product-media-gallery-thumbs button.active");
    if (!activeThumb) return;
    const left = activeThumb.offsetLeft - activeThumb.clientWidth * 2;
    track.scrollTo({ left: left, behavior: "smooth" });
  }

  handleTouch() {
    let startX = 0;
    let endX = 0;

    this.track.addEventListener("touchstart", (e) => {
      startX = e.touches[0].clientX;
    });

    this.track.addEventListener("touchmove", (e) => {
      endX = e.touches[0].clientX;
    });

    this.track.addEventListener("touchend", () => {
      if (!startX || !endX) return;
      const swipeDistance = startX - endX;

      if (swipeDistance > 10) {
        this.next();
      } else if (swipeDistance < -10) {
        this.prev();
      }
    });
  }

  handleKeyboard() {
    this.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.prev();
      else if (event.key === "ArrowRight") this.next();
    });
  }

  handleSticky() {
    if (window.matchMedia("(min-width: 1200px)").matches) {
      const headerGroup = document.querySelector("#header-group.sticky");
      const stickyHeader = document.querySelector("#header").dataset.stickyHeader;

      switch (stickyHeader) {
        case "none": {
          this.style.top = "20px";
          break;
        }
        case "scroll-up": {
          let oldScroll = window.scrollY;

          window.addEventListener("scroll", () => {
            const newScroll = window.scrollY;
            if (newScroll > oldScroll) {
              if (newScroll > headerGroup.clientHeight) {
                this.style.top = "20px";
              }
            } else if (newScroll < oldScroll) {
              this.style.top = `${headerGroup.clientHeight + 20}px`;
            }

            oldScroll = Math.max(window.scrollY, 0);
          });
          break;
        }
        case "always": {
          this.style.top = `${headerGroup.clientHeight + 20}px`;
          break;
        }
      }
    }
  }

  handleModalGallery() {
    const dialog = this.closest(".product-media-gallery-wrapper").querySelector("dialog");
    if (!dialog) return;

    window.addEventListener("dialog:open", (event) => {
      if (event.detail.dialog.id !== dialog.id) return;

      const index = Number(event.detail.triggerElement.dataset.index || 0);
      const item = dialog.querySelectorAll(".product-media-gallery-modal-item")[index];
      const dialogHeight = dialog.clientHeight;
      const top = item.offsetTop + (item.clientHeight - dialogHeight) / 2;
      dialog.scrollTo({ top: top, behavior: "smooth" });

      dialog.querySelectorAll('video').forEach((video) => {
        video.play();
      });
    });

    window.addEventListener("dialog:close", (event) => {
      if (event.detail.dialog.id !== dialog.id) return;

      dialog.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    });

  }

  handleVariantChange() {
    window.addEventListener(
      "variant:change",
      (event) => {
        const selectedVariant = event.detail;

        if (this.colorOptionPosition) {
          this.filterByColor(selectedVariant[`option${this.colorOptionPosition}`]);
        }

        if (selectedVariant.featured_media) {
          const targetSlide = this.allSlides[selectedVariant.featured_media.position - 1];
          const index = this.slides.indexOf(targetSlide);
          this.go(index !== -1 ? index : 0);
        } else {
          this.go(0);
        }
      },
      false,
    );
  }
}
customElements.define("product-media-gallery", ProductMediaGallery);

class ProductQtySwitcher extends HTMLElement {
  constructor() {
    super();

    this.qtyInput = this.querySelector("input");
    this.minusBtn = this.querySelector("[name='decrease']");
    this.plusBtn = this.querySelector("[name='increase']");

    this.qtyInput.addEventListener("change", () => this.onQtyInputChange());
    this.minusBtn.addEventListener("click", () => this.onMinusBtnClick());
    this.plusBtn.addEventListener("click", () => this.onPlusBtnClick());

    this.handleVariantChange();
  }

  onQtyInputChange() {
    this.validateButtons();
  }

  onMinusBtnClick() {
    this.qtyInput.value = Number(this.qtyInput.value) - 1;
    this.validateButtons();
    this.qtyInput.dispatchEvent(new CustomEvent("change"));
  }

  onPlusBtnClick() {
    this.qtyInput.value = Number(this.qtyInput.value) + 1;
    this.validateButtons();
    this.qtyInput.dispatchEvent(new CustomEvent("change"));
  }

  validateButtons() {
    if (this.qtyInput.getAttribute("min") && Number(this.qtyInput.value) <= Number(this.qtyInput.getAttribute("min"))) {
      this.minusBtn.disabled = true;
      this.qtyInput.value = this.qtyInput.getAttribute("min");
    } else {
      this.minusBtn.disabled = false;
    }
    if (this.qtyInput.getAttribute("max") && Number(this.qtyInput.value) >= Number(this.qtyInput.getAttribute("max"))) {
      this.plusBtn.disabled = true;
      this.qtyInput.value = this.qtyInput.getAttribute("max");
    } else {
      this.plusBtn.disabled = false;
    }
  }

  handleVariantChange() {
    window.addEventListener("variant:newDoc", async (event) => {
      const newDoc = event.detail;
      if (!newDoc) return;

      this.replaceWith(newDoc.querySelector(`product-qty-switcher`));
    });
  }

  onVariantChange(event) {
    if (!this.closest(".product-block-atc")) return;

    const newDoc = event.detail;
    const newQtyInput = newDoc.querySelector(".product-block-atc .qty-switcher input");

    this.qtyInput.setAttribute("max", newQtyInput.getAttribute("max"));
    this.validateButtons();
  }
}
customElements.define("product-qty-switcher", ProductQtySwitcher);

class InventoryBar extends HTMLElement {
  constructor() {
    super();

    this.progressBar = this.querySelector(".progress-bar");
    this.animate();

    window.addEventListener("variant:change", async () => {
      this.style.opacity = ".25";
    });

    window.addEventListener("variant:newDoc", async (event) => {
      const newDoc = event.detail;
      if (!newDoc) return;

      this.replaceWith(newDoc.querySelector(`inventory-bar`));
      this.animate();
    });
  }

  animate() {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width;
    }, 250);
  }

  handleVariantChange() {}
}
customElements.define("inventory-bar", InventoryBar);

class ProductQtyBreaks extends HTMLElement {
  constructor() {
    super();

    this.animateAtcButton();
    this.handleOptionChange();
    this.updateOnVariantChange();
  }

  animateAtcButton() {
    this.querySelectorAll("input[type='radio']").forEach((input) => {
      input.addEventListener("change", () => {
        const atcBtn = this.closest("form").querySelector('button[name="add"]');
        if (!atcBtn) return;

        setTimeout(() => {
          atcBtn.classList.add("animate");
        }, 250);

        setTimeout(() => {
          atcBtn.classList.remove("animate");
        }, 1500);
      });
    });
  }

  handleOptionChange() {
    this.querySelectorAll(".product-qty-break-variants select").forEach((select) => {
      select.addEventListener("change", () => {
        const group = select.closest(".product-qty-break");

        const discountType = group.dataset.discountType;
        const discountPercent = Number(group.dataset.discountPercent);
        const discountValue = Number(group.dataset.discountValue);

        let totalPrice = 0;
        let selectedVariants = "";

        group.querySelectorAll(".product-qty-break-variant").forEach((elem) => {
          const selectedOptions = Array.from(elem.querySelectorAll("select")).reduce((options, select) => [...options, select.value], []);
          console.log("selectedOptions", selectedOptions);

          const variantData = this.closest("product-form").querySelector("product-options").variantsData;
          const selectedVariant = variantData.find((variant) => JSON.stringify(variant.options) === JSON.stringify(selectedOptions));

          if (selectedVariant) {
            totalPrice += selectedVariant.price;
            selectedVariants += `${selectedVariant.id},`;

            if (selectedVariant.available) {
              elem.querySelector(".product-qty-break-variant-sold-out").hidden = true;
            } else {
              elem.querySelector(".product-qty-break-variant-sold-out").hidden = false;
            }
          }
        });

        if (discountType === "percentage") {
          group.querySelector("[data-total").textContent = window.Shopify.formatMoney((totalPrice * (100 - discountPercent)) / 100);

          if (discountPercent > 0) {
            group.querySelector("[data-compare-total").textContent = window.Shopify.formatMoney(totalPrice);
          }

        } else {
          group.querySelector("[data-total").textContent = window.Shopify.formatMoney(totalPrice - discountValue);

          if (discountValue > 0) {
            group.querySelector("[data-compare-total").textContent = window.Shopify.formatMoney(totalPrice);
          }
        }

        group.querySelector("input[type='radio']").value = selectedVariants.slice(0, -1);
      });
    });
  }

  updateOnVariantChange() {
    window.addEventListener("variant:change", async () => {
      this.style.opacity = ".25";
    });

    window.addEventListener("variant:newDoc", async (event) => {
      const newDoc = event.detail;
      if (!newDoc) return;

      this.replaceWith(newDoc.querySelector('product-qty-breaks'));
    });
  }
}
customElements.define("product-qty-breaks", ProductQtyBreaks);
